import React, { useCallback, useReducer } from 'react'
import { Main, MainBody, MainTop, MainTopLeft, MainTopTitle, WhiteBox } from '@/components/Layout'
import { BsrsGrid } from '@/components/BsrsGrid'
import { Input, Label } from 'reactstrap'
import { GridCell, GridColumn as Column } from '@progress/kendo-react-grid'
import context from '@/context'
import { TdActions } from '@/components/tdActions'
import Icon from '@/components/Icon'
import { DatePicker } from '@/components/DatePicker'
import { actionTypes, initialState, reducer } from './reducer'
import ReportTemplateSelector from '@/components/reporting/reportTemplateSelector'
import CheckBox from '@/components/CheckBox'
import { useLocalization } from '@/hooks/localization'
import moment from 'moment'
import { defaultDateFormat } from '@/utils/dateUtilities'
import { MultiSelect } from '@/components/MultiSelect'
import { EditMultipleCycleParameters } from '@/dialogs/editCycleParameters/editMultipleCycleParameters'
import PropTypes from 'prop-types'

const addReportSubmissionCell = ({ isValid, ...props }) => {
  return class AddCommandCell extends GridCell {
    handleAddClick () {
      const { data, name, latinName, snapshotDate, dueDate, extendedDueDate, selectedInstitutions, selectedInstitutionGroups, selectedTemplates, forecasting } = props.reportSubmissionGroup
      const input = {
        name,
        latinName,
        snapshotDate: snapshotDate ? moment(snapshotDate).format('YYYY-MM-DD') : null,
        dueDate: dueDate ? moment(dueDate).format('YYYY-MM-DD') : null,
        extendedDueDate: extendedDueDate ? moment(extendedDueDate).format('YYYY-MM-DD') : null,
        reportGroupId: this.props.dataItem.id,
        institutionIds: selectedInstitutions,
        institutionGroupIds: selectedInstitutionGroups,
        reportIds: selectedTemplates.map(item => item.id),
        forecasting,
        data: data.map(x => {
          return {
            Dates: x.dates.map(d => moment(d).format(defaultDateFormat)),
            Weeks: x.weeks
          }
        })
      }
      props.add(input)
    }
    render () {
      return <TdActions>
        <button disabled={!isValid} className='btn-icon' onClick={() => this.handleAddClick()}><Icon name='plus' /></button>
      </TdActions>
    }
  }
}

function CreateReportSubmissionTemporary (props) {
  const [state, dispatch] = useReducer(reducer, initialState)
  const { localize } = useLocalization()

  const onChangeData = useCallback(data => {
    dispatch({ type: actionTypes.changeData, data })
  }, [])
  const onInstitutionChange = useCallback(({ value }) => dispatch({ type: actionTypes.setInstitution, data: value }), [])
  const onInstitutionGroupChange = useCallback(({ value }) => dispatch({ type: actionTypes.setInstitutionGroup, data: value }), [])
  const onReportTemplatesChange = useCallback(selectedTemplates => dispatch({ type: actionTypes.setSelectedTemplates, data: selectedTemplates }), [])
  const onNameChange = useCallback(e => dispatch({ type: actionTypes.setName, data: e.target.value }), [])
  const onLatinNameChange = useCallback(e => dispatch({ type: actionTypes.setLatinName, data: e.target.value }), [])
  const onSnapshotDataChange = useCallback(value => dispatch({ type: actionTypes.setSnapshotDate, data: value }), [])
  const onDueDateChange = useCallback(value => dispatch({ type: actionTypes.setDueDate, data: value }), [])
  const onExtendedDueDateChange = useCallback(value => dispatch({ type: actionTypes.setExtendedDueDate, data: value }), [])
  const onForecastingChange = useCallback(forecasting => dispatch({ type: actionTypes.setForecasting, data: forecasting }), [])

  const addReportGroupSubmission = useCallback((input) => {
    context.api.reportSubmissionGroupService.addReportGroupSubmission(input)
      .then(() => dispatch({ type: actionTypes.resetForm }))
      .catch()
  }, [])

  return (
    <Main>
      <MainTop>
        <MainTopTitle>Create report group submission (temporary page)</MainTopTitle>
        <MainTopLeft />
      </MainTop>
      <MainBody>
        <React.Fragment>
          {!(state.selectedInstitutions && state.selectedInstitutions.length) && <WhiteBox dataCy='institution-group-select'>
            <Label>Select institution group:</Label>
            <MultiSelect
              dataSource={context.api.institutionGroupService.getLookup}
              textField='latinName'
              valueField='id'
              onChange={onInstitutionGroupChange}
              value={state.selectedInstitutionGroups}
            />
          </WhiteBox>
          }
          {!(state.selectedInstitutionGroups && state.selectedInstitutionGroups.length) &&
            <WhiteBox dataCy='institution-select'>
              <Label>Select institution:</Label>
              <MultiSelect
                dataSource={context.api.institutionService.getLookup}
                textField='latinName'
                valueField='id'
                onChange={onInstitutionChange}
                value={state.selectedInstitutions}
              />
            </WhiteBox>
          }
          <WhiteBox dataCy='template-select'>
            <ReportTemplateSelector
              selectedTemplates={state.selectedTemplates}
              onChange={onReportTemplatesChange}
            />
          </WhiteBox>
          <WhiteBox dataCy='cycle-parameters-select'>
            <EditMultipleCycleParameters onChange={onChangeData} loading={props.loading} saving={props.saving} />
          </WhiteBox>
          <WhiteBox dataCy='date-name'>
            <Label>Name:</Label>
            <Input value={state.name} onChange={onNameChange} />
            <Label>Latin name:</Label>
            <Input value={state.latinName} onChange={onLatinNameChange} />
            <Label>Snapshot Date:</Label>
            <DatePicker value={state.snapshotDate} onChange={onSnapshotDataChange} />
            <React.Fragment><Label>Due date:</Label>
              <DatePicker value={state.dueDate} onChange={onDueDateChange} />
              <Label>Extended due date:</Label>
              <DatePicker value={state.extendedDueDate} onChange={onExtendedDueDateChange} />
              <Label>Forecasting:</Label>
              <CheckBox id='forecasting' checked={state.forecasting} onChange={onForecastingChange} />
            </React.Fragment>
          </WhiteBox>
          <WhiteBox dataCy='report-group-grid'>
            {!state.isValid && <div>All details must be filled!</div>}
            <BsrsGrid
              lastReloadedAt={state.gridKey}
              dataSource={request => context.api.reportGroupsService.getAllTableData(state.forecasting, request)}
              filterable={false}>
              <Column field='name' title='Name' editable={false} />
              <Column field='latinName' title='Latin name' editable={false} />
              <Column cell={addReportSubmissionCell({ isValid: state.isValid, localize, reportSubmissionGroup: state, add: addReportGroupSubmission })} width='50px' editable={false} />
            </BsrsGrid>
          </WhiteBox>
        </React.Fragment>
      </MainBody>
    </Main>)
}

CreateReportSubmissionTemporary.propTypes = {
  loading: PropTypes.bool.isRequired,
  saving: PropTypes.bool.isRequired
}

export default CreateReportSubmissionTemporary



// WEBPACK FOOTER //
// ./src/internal-app/components/administration/test-report-group-submission-creator/create-report-submission.js